import { MessageSquareOff, MessageSquareText, Settings2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";
import { LayoutToggle } from "./LayoutToggle";

export function ViewSettingsPopover({ expanded }: { expanded?: boolean } = {}) {
  const { state, dispatch } = useArchViz();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        title="View settings"
        aria-label="View settings"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "h-9 rounded-lg flex items-center transition-colors",
          expanded ? "w-full justify-start gap-2 px-2" : "w-9 justify-center",
          open
            ? "bg-brand-50 text-ink-heading"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )}
      >
        <Settings2 className="h-[18px] w-[18px] shrink-0" />
        {expanded && <span className="text-sm truncate">View settings</span>}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="View settings"
          className="absolute left-full top-0 ml-2 z-50 w-[240px] rounded-xl border border-border bg-card text-card-foreground shadow-xl p-3 space-y-2.5 text-sm"
        >
          <div className="flex items-center justify-between gap-2">
            <span>Theme</span>
            <ThemeToggle />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span>Font size</span>
            <div className="inline-flex rounded-md border border-border overflow-hidden">
              {(["small", "default", "large"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => dispatch({ type: "SET_LABEL_SCALE", scale: s })}
                  aria-pressed={state.labelScale === s}
                  className={cn(
                    "px-2 py-1 text-xs capitalize",
                    state.labelScale === s ? "bg-foreground text-background" : "hover:bg-muted",
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          {state.drillLevel === 1 && (
            <div className="flex items-center justify-between gap-2">
              <span>Layout</span>
              <LayoutToggle />
            </div>
          )}
          <button
            type="button"
            onClick={() => dispatch({ type: "TOGGLE_LABELS" })}
            className="w-full text-left flex items-center justify-between gap-2 hover:bg-muted/40 -mx-1 px-1 py-1 rounded"
          >
            <span>Edge labels</span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              {state.showLabels ? (
                <MessageSquareOff className="h-3.5 w-3.5" />
              ) : (
                <MessageSquareText className="h-3.5 w-3.5" />
              )}
              {state.showLabels ? "Hide" : "Show"}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
